import { useAutoAnimate } from "@formkit/auto-animate/react";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import map from "lodash/map";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSessionStorage } from "usehooks-ts";
import { z } from "zod";

import { selectCognitoId, selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import {
  selectActiveIgnoredOpportunities,
  selectOpportunitiesFilteredByAppSettings,
} from "@/redux/opportunities/opportunitiesSelectors";
import { useGetAccountInfoQuery } from "@/shared/api/account";
import { useGetOpportunitiesQuery, useUpdateIgnoredLeasesMutation } from "@/shared/api/opportunities";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Button } from "@/shared/components/ui/button";
import { Dialog } from "@/shared/components/ui/dialog";
import { DialogContent } from "@/shared/components/ui/dialog";
import { DialogDescription } from "@/shared/components/ui/dialog";
import { DialogFooter } from "@/shared/components/ui/dialog";
import { DialogHeader } from "@/shared/components/ui/dialog";
import { DialogTitle } from "@/shared/components/ui/dialog";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useCanary } from "@/shared/hooks/useCanary";
import { fromStringToDate } from "@/util/dateUtils";
import { CHECKED_OPPORTUNITIES_SESSION_KEY } from "@/util/sessionKeys";

import LeasesPageControls from "./-components/LeasesPageControls";
import Opportunity from "./-components/Opportunity";
import ReviewPeriodAlert from "./-components/ReviewPeriodAlert";

const searchSchema = z.object({
  unsecuredLeaseEnrollment: z.string().optional(),
});

const OpportunitiesSkeleton = () => (
  <div className="flex flex-col gap-4 md:flex-row">
    <Skeleton className="ml-auto h-9 w-[132px] rounded md:ml-0 md:h-[388px] md:min-w-[200px] md:rounded-2xl" />
    <div className="flex w-full flex-col gap-4">
      <Skeleton className="h-52 rounded-2xl" />
      <Skeleton className="h-52 rounded-2xl" />
      <Skeleton className="h-52 rounded-2xl" />
    </div>
  </div>
);

const OpportunitiesEmptyState = () => {
  return (
    <div
      key="emptyState"
      className="flex flex-grow animate-in flex-col items-center justify-center gap-2 fade-in-0 slide-in-from-bottom-4"
    >
      <CheckBadgeIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`There are no opportunities available at this time.`}</p>
    </div>
  );
};

const OpportunitiesView = () => {
  const [parentRef] = useAutoAnimate();
  const navigate = useNavigate();
  const { unsecuredLeaseEnrollment } = useSearch({ from: "/_authenticated/opportunities/" });
  const eid = useSelector(selectMaybeActiveAccountEid);
  const cognitoId = useSelector(selectCognitoId);
  const accountInfoQuery = useGetAccountInfoQuery(eid || skipToken);
  const activeAccountInfo = accountInfoQuery.data;
  const [, setCheckedOpportunties] = useSessionStorage<string[]>(CHECKED_OPPORTUNITIES_SESSION_KEY, []);

  const [renderedAt] = useState(Date.now);
  const reviewExpiryDate = activeAccountInfo?.review_expiration_date
    ? fromStringToDate(activeAccountInfo.review_expiration_date)
    : new Date(renderedAt);
  const accountInReview = renderedAt < reviewExpiryDate.getTime();
  const hasEndReviewPeriodPermission = useCanary("endReviewPeriod");
  const showReviewPeriodAlert = accountInReview && hasEndReviewPeriodPermission;

  const ignoredOpportunities = useSelector(selectActiveIgnoredOpportunities);
  const [updateIgnored] = useUpdateIgnoredLeasesMutation();

  const opportunitiesQuery = useGetOpportunitiesQuery(eid || skipToken);
  const opportunities = useSelector(selectOpportunitiesFilteredByAppSettings);
  const hasUnsecuredEnrollmentSuccess = unsecuredLeaseEnrollment === "success";
  const [isUnsecuredEnrollmentDialogOpen, setIsUnsecuredEnrollmentDialogOpen] = useState(hasUnsecuredEnrollmentSuccess);

  useEffect(() => {
    if (hasUnsecuredEnrollmentSuccess) {
      setIsUnsecuredEnrollmentDialogOpen(true);
    }
  }, [hasUnsecuredEnrollmentSuccess]);

  // Mark opportunities as checked for this session to turn off pinging effect for an eid
  useEffect(() => {
    setCheckedOpportunties((prev) => [...new Set([...prev, eid])]);
  }, [eid, setCheckedOpportunties]);

  const handleToggleIgnoreOpportunity = useCallback(
    (leaseInstance: string) => {
      let newIgnoredList = [];

      if (!ignoredOpportunities.includes(leaseInstance)) {
        newIgnoredList = [...ignoredOpportunities, leaseInstance];
      } else {
        newIgnoredList = ignoredOpportunities.filter((ignoredOpportunity) => ignoredOpportunity !== leaseInstance);
      }

      updateIgnored({
        cognitoId: cognitoId || "",
        eid,
        ignoredOpportunities: newIgnoredList,
      });
    },
    [ignoredOpportunities, eid, cognitoId, updateIgnored],
  );

  const handleUnsecuredEnrollmentDialogChange = (open: boolean) => {
    setIsUnsecuredEnrollmentDialogOpen(open);

    if (!open && hasUnsecuredEnrollmentSuccess) {
      navigate({ to: "/opportunities", replace: true });
    }
  };

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<OpportunitiesSkeleton />}
      shouldRetry
      queries={[accountInfoQuery, opportunitiesQuery]}
      translatedErrorMessage={t`There was an issue loading your opportunities. Please try again later or contact your RM if the problem persists.`}
    >
      <Dialog open={isUnsecuredEnrollmentDialogOpen} onOpenChange={handleUnsecuredEnrollmentDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t`Agreement Submitted`}</DialogTitle>
            <DialogDescription>{t`You have successfully signed and submitted your unallocated lease agreement and are now opted in to this lease.`}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => handleUnsecuredEnrollmentDialogChange(false)}>{t`Close`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-col gap-4 md:gap-9">
        {showReviewPeriodAlert && <ReviewPeriodAlert key={eid} account={activeAccountInfo!} />}
        <div className="relative flex flex-col gap-4 md:flex-row">
          <LeasesPageControls />
          {opportunities.length === 0 ? (
            <OpportunitiesEmptyState />
          ) : (
            <ul ref={parentRef}>
              {map(opportunities, (opportunity) => (
                <Opportunity
                  key={opportunity.lease_instance}
                  opportunity={opportunity}
                  isIgnored={ignoredOpportunities.includes(opportunity.lease_instance)}
                  onIgnoreToggle={handleToggleIgnoreOpportunity}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </ApiErrorBoundary>
  );
};

export const Route = createFileRoute("/_authenticated/opportunities/")({
  component: OpportunitiesView,
  validateSearch: searchSchema,
});
